import React from "react";
import { Box, Typography } from "@mui/material";
import { useToggleRefinement } from "react-instantsearch";
import { usePostHog } from "posthog-js/react";
import { CustomSwitch, directoryFiltersStyles } from "../../pages/styles";

/**
 * Filter configuration for ingredient categories.
 * Attribute names must match the boolean fields indexed in Algolia.
 */
const ingredientCategories = [
  {
    attribute: "carbohydrates-polysaccharides",
    label: "Carbohydrates/ Polysaccharides",
  },
  { attribute: "proteins", label: "Proteins" },
  {
    attribute: "lipids-fats-waxes-oils",
    label: "Lipids / Fats / Waxes / Oils",
  },
  { attribute: "minerals-salts", label: "Minerals and salts" },
  { attribute: "natural-composites", label: "Natural Composites" },
  { attribute: "other", label: "Other" },
];

/**
 * Filter configuration for process types.
 * Attribute names must match the boolean fields indexed in Algolia (prefixed with 'process-').
 */
const processTypes = [
  { attribute: "process-bubbles-removed", label: "Bubbles removed" },
  { attribute: "process-casted", label: "Casted" },
  { attribute: "process-cold-pressed", label: "Cold Pressed" },
  { attribute: "process-cut", label: "Cut" },
  { attribute: "process-dried", label: "Dried" },
  { attribute: "process-extruded", label: "Extruded" },
  { attribute: "process-fermented", label: "Fermented" },
  { attribute: "process-fired", label: "Fired" },
  { attribute: "process-foamed", label: "Foamed" },
  { attribute: "process-frozen", label: "Frozen" },
  { attribute: "process-heated", label: "Heated" },
  { attribute: "process-mixed", label: "Mixed" },
  { attribute: "process-molded", label: "Molded" },
  { attribute: "process-pressed", label: "Pressed" },
];

/**
 * FilterToggle Component
 *
 * Renders a toggle switch for a given filter attribute.
 * Integrates with Algolia's InstantSearch via useToggleRefinement.
 */
const FilterToggle = ({
  attribute,
  label,
  onToggle,
}: {
  attribute: string;
  label: string;
  onToggle: () => void;
}) => {
  const { value, refine } = useToggleRefinement({ attribute });

  const handleToggle = () => {
    refine(value);
    onToggle();
  };

  return (
    <Box sx={directoryFiltersStyles.toggleContainer}>
      <Typography variant="body2">{label}</Typography>
      <CustomSwitch
        checked={value.isRefined}
        onChange={handleToggle}
        focusVisibleClassName=".Mui-focusVisible"
        disableRipple
      />
    </Box>
  );
};

/**
 * RecipeFilters Component
 *
 * Displays filter options for recipes organized by ingredient categories and process types.
 * All filters are functional via Algolia toggle refinements.
 */
const RecipeFilters: React.FC = () => {
  const posthog = usePostHog();

  const handleFilterToggle = (
    attribute: string,
    type: "ingredient" | "process"
  ) => {
    posthog.capture("materials-database-filter", { attribute, type });
  };

  return (
    <Box sx={directoryFiltersStyles.container}>
      {/* Ingredients Filter Group */}
      <Box sx={directoryFiltersStyles.filterGroup}>
        <Typography
          variant="subtitle2"
          sx={directoryFiltersStyles.filterGroupTitle}
        >
          Ingredients
        </Typography>
        {ingredientCategories.map((category) => (
          <FilterToggle
            key={category.attribute}
            attribute={category.attribute}
            label={category.label}
            onToggle={() =>
              handleFilterToggle(category.attribute, "ingredient")
            }
          />
        ))}
      </Box>

      {/* Processes Filter Group */}
      <Box sx={directoryFiltersStyles.filterGroup}>
        <Typography
          variant="subtitle2"
          sx={directoryFiltersStyles.filterGroupTitle}
        >
          Processes
        </Typography>
        {processTypes.map((process) => (
          <FilterToggle
            key={process.attribute}
            attribute={process.attribute}
            label={process.label}
            onToggle={() => handleFilterToggle(process.attribute, "process")}
          />
        ))}
      </Box>
    </Box>
  );
};

export default RecipeFilters;
